import React, { useState, useRef, useEffect } from 'react';
import DottedMap from 'dotted-map';

const WorldMapComponent = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const [pinPosition, setPinPosition] = useState({ x: 0, y: 0 });
  const mapRef = useRef(null);

  // Create map and get Delhi coordinates
  const map = new DottedMap({
    height: 50,
    grid: 'diagonal'
  });

  // Generate SVG with just the world map dots (no pins)
  const svgMap = map.getSVG({
    radius: 0.22,
    color: '#dddddd',
    shape: 'circle',
    backgroundColor: '#ffffff'
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (mapRef.current) {
        const rect = mapRef.current.getBoundingClientRect();
        setMapDimensions({ width: rect.width, height: rect.height });
        
        // Get actual Delhi coordinates from the DottedMap
        const delhiCoords = map.getPin({ lat: 28.6139, lng: 77.2090 });
        
        // Scale the coordinates to match the actual rendered image size
        const scaleX = rect.width / map.image.width;
        const scaleY = rect.height / map.image.height;
        
        setPinPosition({
          x: delhiCoords.x * scaleX,
          y: delhiCoords.y * scaleY
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    // Also update when the component mounts
    const timer = setTimeout(updateDimensions, 100);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
      clearTimeout(timer);
    };
  }, []);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setTimeout(() => {
      if (!isHovered) {
        setShowTooltip(false);
      }
    }, 150);
  };

  const handleTooltipMouseEnter = () => {
    setIsHovered(true);
    setShowTooltip(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        ref={mapRef}
        style={{ position: 'relative', display: 'block', width: '100%' }}
      >
        {/* Base map */}
        <img 
          src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
          alt="World Map"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        
        {/* Interactive Delhi pin overlay - positioned using actual coordinates */}
        {pinPosition.x > 0 && pinPosition.y > 0 && (
          <div
            style={{
              position: 'absolute',
              left: `${pinPosition.x}px`,
              top: `${pinPosition.y}px`,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#6b46c1',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
              zIndex: 10,
              border: '3px solid white',
              boxShadow: '0 4px 12px rgba(107, 70, 193, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => window.open('https://www.google.com/maps/dir/28.5471266,77.2951152/DLE+Industrial+Area,+Kirti+Nagar,+Delhi,+110015/@28.5970021,77.0613086,11z/data=!3m1!4b1!4m19!1m8!3m7!1s0x390d03014d873459:0x72609d194ff62637!2sDLE+Industrial+Area,+Kirti+Nagar,+Delhi,+110015!3b1!8m2!3d28.657142!4d77.1434831!16s%2Fg%2F12hlxvc53!4m9!1m1!4e1!1m5!1m1!1s0x390d03014d873459:0x72609d194ff62637!2m2!1d77.1434831!2d28.657142!3e0?entry=ttu&g_ep=EgoyMDI1MDUyMS4wIKXMDSoASAFQAw%3D%3D', '_blank')}
          />
        )}
      </div>
      
      {/* Tooltip */}
      {showTooltip && pinPosition.x > 0 && (
        <div
          style={{
            position: 'absolute',
            left: `${pinPosition.x - 100}px`,
            top: `${pinPosition.y - 100}px`,
            backgroundColor: isHovered ? '#1a1a2e' : '#f8f9fa',
            color: isHovered ? 'white' : '#333',
            border: isHovered ? 'none' : '1px solid #e9ecef',
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            fontSize: '14px',
            lineHeight: '1.5',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            minWidth: '220px',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onClick={() => window.open('https://www.google.com/maps/dir/28.5471266,77.2951152/DLE+Industrial+Area,+Kirti+Nagar,+Delhi,+110015/@28.5970021,77.0613086,11z/data=!3m1!4b1!4m19!1m8!3m7!1s0x390d03014d873459:0x72609d194ff62637!2sDLE+Industrial+Area,+Kirti+Nagar,+Delhi,+110015!3b1!8m2!3d28.657142!4d77.1434831!16s%2Fg%2F12hlxvc53!4m9!1m1!4e1!1m5!1m1!1s0x390d03014d873459:0x72609d194ff62637!2m2!1d77.1434831!2d28.657142!3e0?entry=ttu&g_ep=EgoyMDI1MDUyMS4wIKXMDSoASAFQAw%3D%3D', '_blank')}
        >
          <div style={{ 
            fontWeight: '600', 
            marginBottom: '8px', 
            color: isHovered ? '#8b5cf6' : '#6b46c1',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            Visit Us
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: isHovered ? '#8b5cf6' : '#6b46c1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.3s ease'
            }}>
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
          <div style={{ 
            fontSize: '13px', 
            color: isHovered ? '#d1d5db' : '#6b7280',
            lineHeight: '1.4'
          }}>
            DLF INDUSTRIAL AREA<br />
            KIRTI NAGAR<br />
            NEW DELHI 110015
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMapComponent;